import { useMemo } from "react";
import type { LargeTrade } from "@/api/polymarketApi";
import { Chip, Empty, GridNote, OutcomeLead, OutcomeTag, Panel, SearchBox, Select, SourceError, WalletLink } from "../components/Bits";
import { type Col, DataGrid } from "../components/DataGrid";
import { View } from "../components/Shell";
import { ViewHeader } from "../components/ViewHeader";
import { isoSeconds } from "../lib/csv";
import { TRADES_REFRESH_MS, useLargeTrades } from "../lib/data";
import { Updated } from "../lib/freshness";
import { ago, blankText, cents, count, usd } from "../lib/format";
import { matches } from "../lib/markets";
import { openMarket, useParam, useSearchParam } from "../lib/nav";
import { tradeUsd } from "../lib/whales";

export const SIZE_OPTIONS = [
	{ value: "10000", label: "≥ $10K" },
	{ value: "25000", label: "≥ $25K" },
	{ value: "50000", label: "≥ $50K" },
	{ value: "100000", label: "≥ $100K" },
];

const SIDE_OPTIONS = [
	{ value: "all", label: "Side: all" },
	{ value: "BUY", label: "Buys" },
	{ value: "SELL", label: "Sells" },
];

const COLS: Col<LargeTrade>[] = [
	{ key: "t", label: "Age", title: "Sorted by trade time", w: 34, right: true, wideOnly: true, sort: (r) => r.timestamp, csv: (r) => isoSeconds(r.timestamp), cell: (r) => <span className="text-dim">{ago(r.timestamp)}</span> },
	{ key: "q", label: "Market", w: 150, grow: 3, sort: (r) => r.title, cell: (r, narrow) => <span className={narrow ? "clamp-2 text-bright" : "text-bright"} title={r.title}>{narrow ? <><span className="text-dim">{ago(r.timestamp)} · </span><OutcomeLead name={r.outcome} /></> : null}{r.title}</span> },
	{ key: "o", label: "Outcome", w: 96, grow: 1, prio: 2, csv: (r) => r.outcome, cell: (r) => <OutcomeTag name={r.outcome} /> },
	{ key: "s", label: "Side", title: "Whether the taker bought or sold", w: 34, sort: (r) => r.side, cell: (r) => <Chip tone={r.side === "BUY" ? "up" : "dn"}>{r.side}</Chip> },
	{ key: "u", label: "USD", title: "Shares times price", w: 56, right: true, sort: tradeUsd, cell: (r) => <span className="font-bold text-bright">{usd(tradeUsd(r))}</span> },
	{ key: "p", label: "Price", w: 46, right: true, prio: 1, sort: (r) => r.price, cell: (r) => cents(r.price) },
	{ key: "w", label: "Wallet", w: 120, grow: 1, prio: 3, sort: (r) => r.name || r.pseudonym || r.proxy_wallet, csv: (r) => r.proxy_wallet, cell: (r) => <WalletLink address={r.proxy_wallet} label={r.name || r.pseudonym} /> },
];

export function WhaleTradesView() {
	const [minUsd, setMinUsd] = useParam("min", "10000");
	const [side, setSide] = useParam("side", "all");
	const [q, setQ] = useSearchParam();
	const { data, error, isLoading, mutate, updatedAt } = useLargeTrades(Number(minUsd));
	const rows = data ?? [];
	const shown = useMemo(
		() => rows.filter((r) => (side === "all" || r.side === side) && matches(q, r.title, r.outcome, r.name, r.pseudonym, r.proxy_wallet)),
		[rows, side, q],
	);
	const total = useMemo(() => shown.reduce((s, r) => s + tradeUsd(r), 0), [shown]);

	const body = error && !data ? (
		<SourceError source="Polymarket (data-api)" onRetry={() => void mutate()} />
	) : data && shown.length === 0 ? (
		<Empty>No trades match.</Empty>
	) : undefined;

	return (
		<View>
			<ViewHeader
				title="large trades"
				meta={data ? `${count(shown.length)} trades · ${usd(total)}` : blankText(19)}
				desc="Newest taker trades above the size floor. Click a row for the market. Wallet names are the profiles' public handles."
			>
				<SearchBox value={q} onChange={setQ} placeholder="search market, wallet" />
				<Select label="Minimum size" value={minUsd} options={SIZE_OPTIONS} onChange={setMinUsd} />
				<Select label="Side" value={side} options={SIDE_OPTIONS} onChange={setSide} />
			</ViewHeader>
			<Panel>
				<DataGrid
					label="Large trades"
					cols={COLS}
					rows={shown}
					rowKey={(r) => `${r.transaction_hash}:${r.token_id}:${r.proxy_wallet}:${r.size}`}
					initialSort={{ key: "t", dir: "desc" }}
					csvName="large-trades"
					copyValue={(r) => r.proxy_wallet}
					onRow={(r) => openMarket(r.condition_id)}
					loading={isLoading && !data}
					body={body}
				/>
				<GridNote className="min-h-[41px] sm:min-h-0"><Updated at={updatedAt} refreshMs={TRADES_REFRESH_MS} failing={Boolean(error)} />last {count(rows.length)} trades at this floor · source: Polymarket data-api</GridNote>
			</Panel>
		</View>
	);
}
